import {
  getLockValue,
  getSettingsDataInstance,
  getShadowFabricInstance,
  unFocusObjects,
  updateBoundingBoxInfo
} from '@frontend/utils';
import { TOC_LINE_PREFIX_ID } from '@frontend/constants/templates-field-prefixes';
import { ShowPageScopeCollector } from '@frontend/group/modules/scope-collectors/show-page-collector';
import parseText from '@/common/preview/parse-text';
import { FIELD_TYPE_MULTI_LINE_TEXT } from '@frontend/constants/type-fields-of-template';
import { scaleControlsCornerSize } from '@frontend/constants/fabric-constants';
import { formDataToObject } from '@frontend/lib/utils/form-data-to-object';
import { fabric } from 'fabric';
import { getGroupNameByAddFieldId } from '@/common/preview/draw-smart-object';
import { parseJsonData } from '@frontend/services/helpers';
import {
  waitForFont,
  waitForFontsLoaded,
} from '@/common/preview/draw-multi-line-text';

const getScopePage = () => ShowPageScopeCollector.get(['canvas_data', 'updateLayoutChanges']);

function isTocLineObjectId(id) {
  return typeof id === 'string'
    && (id.startsWith(TOC_LINE_PREFIX_ID));
}

function measureTextWidth(text, style) {
  const t = new fabric.Text(String(text ?? ''), {
    fontFamily: style.fontFamily,
    fontSize: style.fontSize,
    fontWeight: style.fontWeight,
    fontStyle: style.fontStyle,
    charSpacing: style.charSpacing,
    strokeWidth: 0,
    shadow: null,
  });

  return t.width || 0;
}

function toNormalizedStyles(styles) {
  if (!styles) {
    return [];
  }

  let normalized = styles;

  if (!Array.isArray(normalized)) {
    normalized = Object.values(normalized).map((inner) => Object.values(inner || {}));
  }

  const isStyleObj = (value) => (
    value
    && typeof value === 'object'
    && !Array.isArray(value)
    && Object.keys(value).length > 0
  );

  return normalized
    .map((line) => (Array.isArray(line) ? line.filter(isStyleObj) : []));
}

function createDotsText(availableWidth, dotWidth) {
  if (availableWidth <= 0 || dotWidth <= 0) {
    return '';
  }

  const maxGuess = Math.max(0, Math.floor((availableWidth / dotWidth) + 4));

  let lo = 0;
  let hi = maxGuess;

  while (lo < hi) {
    const mid = Math.floor((lo + hi + 1) / 2);
    if ((dotWidth * mid) <= (availableWidth + 0.5)) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }

  return '.'.repeat(lo);
}

function attachTocRenderer(textBox) {
  if (textBox.__tocRendererAttached) {
    return;
  }

  const originalRender = textBox._render;

  textBox._render = function (ctx) {
    originalRender.call(this, ctx);

    const pageNumber = String(this.tocPageNumber ?? '').trim();
    if (!pageNumber) {
      return;
    }

    const lines = this._textLines || [];
    if (!lines.length) {
      return;
    }

    const lastLineIndex = lines.length - 1;
    const lineWidth = Number.isFinite(this.getLineWidth?.(lastLineIndex))
      ? this.getLineWidth(lastLineIndex)
      : measureTextWidth(
        Array.isArray(lines[lastLineIndex]) ? lines[lastLineIndex].join('') : lines[lastLineIndex],
        {
          fontFamily: this.fontFamily,
          fontSize: this.fontSize,
          fontWeight: this.fontWeight || 'normal',
          fontStyle: this.fontStyle || 'normal',
          charSpacing: +this.charSpacing || 0,
        }
      );

    const textStyle = {
      fontFamily: this.fontFamily,
      fontSize: this.fontSize,
      fontWeight: this.fontWeight || 'normal',
      fontStyle: this.fontStyle || 'normal',
      charSpacing: +this.charSpacing || 0,
    };

    const numberWidth = Math.max(1, measureTextWidth(pageNumber, textStyle));
    const dotWidth = Math.max(1, measureTextWidth('.', textStyle));
    const sideGap = Math.max(1, measureTextWidth(' ', textStyle));

    const leftOffset = typeof this._getLeftOffset === 'function' ? this._getLeftOffset() : -this.width / 2;
    const topOffset = typeof this._getTopOffset === 'function' ? this._getTopOffset() : -this.height / 2;
    const lineLeftOffset = typeof this._getLineLeftOffset === 'function' ? this._getLineLeftOffset(lastLineIndex) : 0;

    const numberRight = leftOffset + this.width;
    const numberLeft = numberRight - numberWidth;

    const dotsLeft = leftOffset + lineLeftOffset + lineWidth + sideGap;
    const dotsRight = numberLeft - sideGap;
    const availableDotsWidth = Math.max(0, dotsRight - dotsLeft);

    const dotsText = createDotsText(availableDotsWidth, dotWidth);

    let lineHeights = 0;
    for (let i = 0; i < lastLineIndex; i++) {
      lineHeights += this.getHeightOfLine(i);
    }

    const heightOfLastLine = this.getHeightOfLine(lastLineIndex);
    const textLineHeight = Number(this.lineHeight) || 1;
    const maxHeight = heightOfLastLine / textLineHeight;
    const fontSizeFraction = Number(this._fontSizeFraction ?? 0.222);
    const baseline = topOffset + lineHeights + (maxHeight * (1 - fontSizeFraction));

    ctx.save();
    ctx.font = this._getFontDeclaration();
    ctx.fillStyle = this.fill;
    ctx.textBaseline = 'alphabetic';
    ctx.direction = this.direction || 'ltr';

    if (dotsText) {
      ctx.textAlign = 'left';
      ctx.fillText(dotsText, dotsLeft, baseline);
    }

    ctx.textAlign = 'right';
    ctx.fillText(pageNumber, numberRight, baseline);
    ctx.restore();
  };

  textBox.refreshTocLine = function () {
    this.dirty = true;
    this.setCoords();
    this.canvas?.requestRenderAll();
  };

  textBox.__tocRendererAttached = true;
}

function initCoords(canvas_data, field, id) {
  const boundingData = parseJsonData(canvas_data[id]._data[`${field}_bounding_data`] || '{}');
  const forceBoundingData = canvas_data[id]._data[`${field}_force_bounding_data`] ?? false;

  const x = forceBoundingData ? parseFloat(boundingData?.left || 0) : 0;
  const y = forceBoundingData ? parseFloat(boundingData?.top || 0) : 0;

  return { x, y };
}

function resolveTocLineOrder({ canvas_data, id, field, index }) {
  const userOrderRaw = canvas_data[id]._data.users_fields_order;
  const userOrder = userOrderRaw ? JSON.parse(userOrderRaw) : null;

  if (Array.isArray(userOrder)) {
    const userIndex = userOrder.indexOf(field);
    if (userIndex >= 0) {
      return userIndex + 1;
    }
  }

  const storedOrder = Number(canvas_data[id]._data[`${field}_order`]);
  if (Number.isFinite(storedOrder) && storedOrder !== 0) {
    return storedOrder;
  }

  return index + 1;
}

export async function drawTocLine({ canvas, id, previewBoundingBoxData }) {
  unFocusObjects();

  canvas.getObjects().forEach((obj) => {
    if (isTocLineObjectId(obj.id)) {
      canvas.remove(obj);
    }
  });

  const { canvas_data } = getScopePage();
  const formData = new FormData();

  formData.append('type', TOC_LINE_PREFIX_ID);

  const settingsTemplate = getSettingsDataInstance(canvas_data[id]._data?.settings_template);

  for (let index = 0; index < canvas_data[id].tocLines.length; index++) {
    const field = canvas_data[id].tocLines[index];

    const offset_x = canvas_data[id]._data[field + '_offset_x'] || '0';
    const offset_y = canvas_data[id]._data[field + '_offset_y'] || '0';
    const angle = canvas_data[id]._data[field + '_angle'] || '0';
    const color = canvas_data[id]._data[field + '_color'] || '#000000';
    const font = canvas_data[id]._data[field + '_font'] || 'Proxima-Nova-Semibold';
    const width = parseInt(canvas_data[id]._data[field + '_width'], 10) || 300;
    const fontSize = parseInt(canvas_data[id]._data[field + '_fontsize'], 10) || 40;
    const lineHeight = parseFloat(canvas_data[id]._data[field + '_leading']) || 1.16;
    const charSpacing = Number(canvas_data[id]._data[field + '_tracking'] || 0);
    const opacity = canvas_data[id]._data[field + '_opacity'] || '1';
    const visible = canvas_data[id]._data[field + '_visible'] ?? true;

    const { x, y } = initCoords(canvas_data, field, id);

    const rawText = canvas_data[id]._data[field] || '';
    const { text, styles: parsedStyles } = parseText(rawText, color);

    const additionalStyles = toNormalizedStyles(canvas_data[id]._data[field + '_styles'] || []);
    const styles = additionalStyles.length > 0 ? additionalStyles : parsedStyles;

    const pageNumber = canvas_data[id]._data[field + '_page_number'] || '';

    if (!text && !pageNumber) {
      continue;
    }

    await waitForFont(font);

    const fonts = new Set();
    for (const lineIndex in styles) {
      const line = styles[lineIndex];
      for (const charIndex in line) {
        const style = line[charIndex];
        if (style?.fontFamily) {
          fonts.add(`${style.fontSize || fontSize}px ${style.fontFamily}`);
        }
      }
    }

    if (fonts.size) {
      await waitForFontsLoaded(Array.from(fonts));
    }

    const objectId = field;
    const order = resolveTocLineOrder({
      canvas_data,
      id,
      field: objectId,
      index,
    });

    const textBox = new fabric.Textbox(text, {
      id: objectId,
      element_id: field,
      top: y + parseFloat(offset_y),
      left: x + parseFloat(offset_x),
      width,
      fontFamily: font,
      fontSize,
      fill: color,
      selectable: true,
      evented: true,
      editable: true,
      textAlign: 'left',
      lineHeight,
      charSpacing,
      fontSelector: true,
      colorSelector: true,
      fontThemeSelector: false,
      fontColor: color,
      order,
      opacity,
      name: canvas_data[id]._data[field + '_name']
        || settingsTemplate.getNameById(objectId, canvas_data[id]._data[field + '_name'] || '')
        || `TOC Line ${index + 1}`,
      link: settingsTemplate.getLinkById(objectId, ''),
      fieldType: FIELD_TYPE_MULTI_LINE_TEXT,
      styles,
      shadow: getShadowFabricInstance(id, field),
      locked: getLockValue(id, field),
      angle: angle ? parseFloat(angle) : 0,
      visible,
      groupName: getGroupNameByAddFieldId(objectId, canvas_data[id]),
      maxChars: Number(canvas_data[id]._data[`${field}_max_chars`] || 10000),
      updateLayoutChanges: getScopePage().updateLayoutChanges,
      isTocLine: true,
      tocPageNumber: pageNumber,
      objectCaching: false,
    });

    attachTocRenderer(textBox);

    textBox.setControlsVisibility({
      tr: true,
      br: true,
      bl: true,
      ml: true,
      mt: false,
      mr: true,
      mb: false,
      mtr: true,
    });

    scaleControlsCornerSize(textBox);

    canvas.add(textBox);
    textBox.setCoords();

    const boundingBox = textBox.getBoundingRect();
    const lineHeights = Math.max(...(textBox.__lineHeights || [textBox.height || 0]));

    formData.append('element_ids[]', field);
    formData.append(`${field}_bounding_data`, JSON.stringify(boundingBox));
    formData.append(`${field}_width`, textBox.width);
    formData.append(`${field}_height`, textBox.height);
    formData.append(`${field}_line_heights`, lineHeights.toFixed(2));
  }

  if (previewBoundingBoxData) {
    previewBoundingBoxData.data.push(formDataToObject(formData));
  } else {
    updateBoundingBoxInfo(formData, id);
  }
}
